import { useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
  Badge,
  Checkbox,
  Table,
  Button,
  Modal
} from '@burna/monster-design-system';
import axios from 'axios';
import useUser from 'hooks/useUser';
import { APIName } from 'config';
import styles from './index.module.scss';
import RowItemFileList from 'components/row-item-file-list';
import EmptyMessage from 'components/empty-message';
import { usePageWidthLessThan } from 'hooks/usePageWidthLessThan';
import useDownload from 'hooks/useDownload';
import { getFileSizeString } from 'utils';
import moment from 'moment';
import CopyIcon from 'components/copy-icon';

function FileList() {
  const [isTabletSize] = usePageWidthLessThan();
  const history = useHistory();
  const user = useUser();
  const [data, setData] = useState(null);
  const [selectedPaths, setSelectedPaths] = useState(new Set());
  const [errors, setErrors] = useState(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [inSelectMode, setIsSelectMode] = useState(false);
  const { startDownload } = useDownload();

  useEffect(() => {
    if (!user.username) {
      history.push('/');
      return;
    }
    axios
      .post(`/api/${APIName}/fileList`)
      .then(({ data }) => {
        setData(
          data.map(item => ({
            ...item,
            downloadPath: location.origin + item.downloadPath,
            managePath: location.origin + item.managePath
          }))
        );
      })
      .catch(error => {
        setTimeout(
          () => window.alert('An unexpected error occurred during file login'),
          500
        );
        history.push('/');
      });
  }, []);

  const handleSelect = item => selected => {
    const newPaths = new Set(selectedPaths);

    if (selected) {
      newPaths.add(item.managePath);
    } else {
      newPaths.delete(item.managePath);
    }
    setSelectedPaths(newPaths);

    if (newPaths.size === 0) {
      setIsSelectMode(false);
    }
  };

  const handleHoldItem = item => {
    setIsSelectMode(true);
    const newPaths = new Set(selectedPaths);
    newPaths.add(item.managePath);
    setSelectedPaths(newPaths);
  };

  const handleSwipe = item => {
    const newPaths = new Set();
    newPaths.add(item.managePath);
    setSelectedPaths(newPaths);
    setConfirmDeleteOpen(true);
  };

  const handleSelectAll = selected => {
    setSelectedPaths(
      new Set(selected ? data.map(item => item.managePath) : null)
    );

    if (!selected) {
      setIsSelectMode(false);
    }
  };

  const handleDownload = downloadPath => {
    startDownload({
      downloadPath,
      showErrorAlert: true
    });
  };

  const handleDelete = async () => {
    if (deleting) {
      return;
    }
    setDeleting(true);
    try {
      const { data: result } = await axios.delete(`/api/${APIName}/delete`, {
        data: {
          managePaths: [...selectedPaths]
        }
      });
      setData(
        data.filter(
          item =>
            !selectedPaths.has(item.managePath) ||
            result[item.managePath] !== 200
        )
      );
      const errors = data.filter(
        item =>
          selectedPaths.has(item.managePath) && result[item.managePath] !== 200
      );
      setSelectedPaths(new Set());
      setIsSelectMode(false);
      if (errors.size > 0) {
        setErrors(new Set([...errors]));
        setTimeout(() => setErrors(new Set()), 3000);
      }
    } catch (e) {
      setSelectedPaths(new Set());
      setErrors(new Set([...selectedPaths]));
      setTimeout(() => setErrors(new Set()), 3000);
      setIsSelectMode(false);
    }
    setDeleting(false);
    setConfirmDeleteOpen(false);
  };

  return (
    <section className={styles.wrapper}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h3>Manage File List</h3>
          <div className={styles.actions}>
            <Button
              className={styles.button}
              text={`Removes (${selectedPaths.size})`}
              icon="trash-2"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={selectedPaths.size === 0}
              destructive
            />
            <Button
              className={styles.button}
              text="Upload"
              icon="upload"
              as={Link}
              to="/"
            />
          </div>
        </header>
        {isTabletSize ? (
          <>
            {data?.length > 0 ? (
              <>
                {inSelectMode ? (
                  <div className={styles.selectModeContainer}>
                    <form>
                      <Checkbox
                        text="Select All"
                        checked={selectedPaths.size === data.length}
                        onChange={handleSelectAll}
                      />
                    </form>
                  </div>
                ) : null}
                <RowItemFileList
                  data={data}
                  inSelectMode={inSelectMode}
                  selectedPaths={selectedPaths}
                  handleSelect={handleSelect}
                  onHold={handleHoldItem}
                  onSwipe={handleSwipe}
                />
              </>
            ) : (
              <EmptyMessage className={styles.emptyMesage} />
            )}
          </>
        ) : (
          <>
            {data && (
              <>
                <div className={styles.table}>
                  <Table
                    rowsPerPage={7}
                    columnDefinitions={[
                      {
                        type: 'checkbox',
                        checked: selectedPaths.size === data.length,
                        onChange: handleSelectAll
                      },
                      { type: 'text', header: '#' },
                      { type: 'text', header: 'File name' },
                      { type: 'text', header: 'File Size' },
                      { type: 'text', header: 'Upload Date' },
                      { type: 'text', header: 'Last Download' },
                      { type: 'action-icons', header: 'URL' }
                    ]}>
                    {data.map((item, i) => (
                      <Table.Row
                        key={i}
                        selected={selectedPaths.has(item.managePath)}
                        error={errors.has(item.managePath)}>
                        <Table.Cell
                          checked={selectedPaths.has(item.managePath)}
                          onChange={handleSelect(item)}
                        />
                        <Table.Cell>{i + 1}</Table.Cell>
                        <Table.Cell>
                          <div className={styles.fileName}>
                            {item.managePath.substring(
                              item.managePath.lastIndexOf('/') + 1
                            )}
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          {getFileSizeString(item.fileSize)}
                        </Table.Cell>
                        <Table.Cell>
                          {moment(item.uploadDate).format('MMM DD, YYYY')}
                          <br />
                          <br />
                          {moment(item.uploadDate).format('hh:mm A')}
                        </Table.Cell>
                        {item.lastDownloadDate ? (
                          <Table.Cell>
                            {moment(item.lastDownloadDate).format(
                              'MMM DD, YYYY'
                            )}
                            <br />
                            <br />
                            {moment(item.lastDownloadDate).format('hh:mm A')}
                          </Table.Cell>
                        ) : (
                          <Table.Cell empty>
                            <Badge
                              size="md"
                              type="success"
                              label="Ready to download"
                              icon="arrow-right"
                              iconPlacement="trailing"
                            />
                          </Table.Cell>
                        )}
                        <Table.Cell>
                          <Table.ActionIcon
                            icon="download"
                            onClick={() => handleDownload(item.downloadPath)}
                          />
                          <Table.ActionIcon
                            icon="external-link"
                            as={Link}
                            to={new URL(item.managePath).pathname}
                            target="_blank"
                          />
                          <CopyIcon
                            value={item.downloadPath.replace(
                              /(?!^) (?!$)/g,
                              '%20'
                            )}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <Modal
        type="error"
        title={'Delete the selected item' + (selectedPaths.size > 1 ? 's' : '')}
        description={
          deleting
            ? 'Deleting...'
            : `Are you sure you want to delete ${
                selectedPaths.size > 1
                  ? `these ${selectedPaths.size} items`
                  : 'this item'
              }? This action cannot be undone.`
        }
        confirmLabel="Delete"
        closeLabel="Cancel"
        open={confirmDeleteOpen}
        onConfirm={handleDelete}
        onClose={() => {
          setSelectedPaths(new Set());
          setIsSelectMode(false);
          setConfirmDeleteOpen(false);
        }}
      />
    </section>
  );
}

export default FileList;
